import React from "react";
import { OvalLoader } from "../../../components/common/Loader";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { styleExcelHeaderRow } from "../../../utils/excelStyles";

// Custom Medal SVG Component
const MedalSVG = ({ color = "#1a4331" }) => (
  <svg
    className="top-achiever-medal-svg"
    width="54"
    height="72"
    viewBox="0 0 54 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: "absolute",
      top: "0px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 10,
      transition: "transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    }}
  >
    {/* Ribbon */}
    <path d="M17 0H37V32L27 24L17 32V0Z" fill="#0d2417" />
    <path d="M17 0H27V24L17 32V0Z" fill="#1a4331" />
    {/* Medal Base (Outer glow) */}
    <circle cx="27" cy="45" r="21" fill="#fef08a" />
    {/* Medal Inner */}
    <circle cx="27" cy="45" r="16" fill="#d97706" />
    {/* Star */}
    <path
      d="M27 35L29.245 40.8541H35.0764L30.3657 44.5918L32.1631 50.4459L27 46.9582L21.8369 50.4459L23.6343 44.5918L18.9236 40.8541H24.755L27 35Z"
      fill="white"
    />
  </svg>
);

const TopAchievers = ({ data, ovalLoading, hasPermission }) => {
  const [excelLoading, setExcelLoading] = React.useState(false);

  const handleDownloadExcel = async () => {
    try {
      setExcelLoading(true);
      const workbook = new Excel.Workbook();
      const workSheetName = "Top Achievers";
      const worksheet = workbook.addWorksheet(workSheetName);

      // Define columns
      worksheet.columns = [
        { header: "S.No", key: "sno", width: 10 },
        { header: "Category", key: "category", width: 20 },
        { header: "Name", key: "name", width: 30 },
        { header: "Company", key: "company", width: 30 },
        { header: "Value", key: "value", width: 15 },
        { header: "Country", key: "country", width: 20 },
        { header: "State", key: "state", width: 20 },
        { header: "Region", key: "region", width: 20 },
        { header: "Chapter", key: "chapter", width: 20 },
      ];

      // Add Header Style
      styleExcelHeaderRow(worksheet.getRow(1), worksheet.columns.length);

      let sno = 1;

      activeCategories.forEach((cat) => {
        if (cat.list && cat.list.length > 0) {
          cat.list.forEach((user, i) => {
            worksheet.addRow({
              sno: sno++,
              category: cat.title,
              name: user[cat.tooltipNameKey] || user[cat.nameKey] || "",
              company: user.user_company_name || "",
              value: user[cat.valKey] || 0,
              country: user.country_name || "",
              state: user.state_name || "",
              region: user.region_name || "",
              chapter: user.chapter_name || "",
            });
          });
        }
      });

      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }
      });

      const buf = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buf]),
        `Top_Achievers_${data?.week_start_date}_to_${data?.week_end_date}.xlsx`,
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.message);
    } finally {
      setExcelLoading(false);
    }
  };

  if (
    !hasPermission("dashboard-tyns-data-view") &&
    !hasPermission("dashboard-fbms-data-view") &&
    !hasPermission("dashboard-rfls-data-view") &&
    !hasPermission("dashboard-visitors-data-view")
  ) {
    return null;
  }

  // Define categories array to dynamically map
  const categories = [
    {
      id: "tyfcbs",
      permission: "dashboard-tyns-data-view",
      title: "Biz Given",
      list: data?.thisWeekTop3AchieversList?.tyfcbsList || [],
      overallTitle: "OVERALL",
      overallValue: data?.thisWeekTop3AchieversList?.thisWeekOverallTyfcb,
      valKey: "tyfcb_amount",
      formatVal: (v) => `${v}`,
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "fbms",
      permission: "dashboard-fbms-data-view",
      title: "FBMs",
      list: data?.thisWeekTop3AchieversList?.onetooneList || [],
      overallTitle: "OVERALL",
      overallValue: data?.thisWeekTop3AchieversList?.thisWeek121s,
      valKey: "one2one_count",
      formatVal: (v) => `${v} Meetings`,
      imgKey: "thumb_from_profile_image",
      nameKey: "from_user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "rfls",
      permission: "dashboard-rfls-data-view",
      title: "RFLs",
      list: data?.thisWeekTop3AchieversList?.referralsList || [],
      overallTitle: "OVERALL",
      overallValue: data?.thisWeekTop3AchieversList?.thisWeekRfls,
      valKey: "referrals_count",
      formatVal: (v) => `${v} Referrals`,
      imgKey: "thumb_from_profile_image",
      nameKey: "from_user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "visitors",
      permission: "dashboard-visitors-data-view",
      title: "General Visitors",
      list: data?.thisWeekTop3AchieversList?.generalVisitorsList || [],
      overallTitle: "OVERALL",
      overallValue: data?.thisWeekTop3AchieversList?.thisWeekVisitors,
      valKey: "visitors_count",
      formatVal: (v) => `${v} Visitors`,
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "chief_guests",
      permission: "dashboard-visitors-data-view",
      title: "Chief Guests",
      list: data?.thisWeekTop3AchieversList?.cheifGuestVisitorsList || [],
      overallTitle: "OVERALL",
      overallValue: data?.thisWeekTop3AchieversList?.thisWeekChiefGuests,
      valKey: "visitors_count",
      formatVal: (v) => `${v} Guests`,
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "power_dates",
      permission: "dashboard-visitors-data-view",
      title: "Power Date",
      list: data?.thisWeekTop3AchieversList?.powerDateVisitorsList || [],
      overallTitle: "OVERALL",
      overallValue: data?.thisWeekTop3AchieversList?.thisWeekPowerDates,
      valKey: "visitors_count",
      formatVal: (v) => `${v} Dates`,
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "inductions",
      permission: "dashboard-visitors-data-view",
      title: "Inductions",
      list: data?.thisWeekTop3AchieversList?.inductionsVisitorsList || [],
      overallTitle: "OVERALL",
      overallValue: data?.thisWeekTop3AchieversList?.thisWeekInductions,
      valKey: "visitors_count",
      formatVal: (v) => `${v} Inductions`,
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
  ];

  // Filter out categories user doesn't have permission for
  const activeCategories = categories.filter((cat) =>
    hasPermission(cat.permission),
  );

  if (activeCategories.length === 0) {
    return null;
  }

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card mb-4 custom-card border-0 shadow-sm rounded-4 overflow-hidden">
          <div
            className="card-header border-0 d-flex justify-content-between align-items-center"
            style={{
              background:
                "linear-gradient(90deg, #176541 0%, #0E4233 45%, #18232B 100%)",
              padding: "28px 32px",
              position: "relative",
              overflow: "hidden",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
            }}
          >
            {/* Glossy wave reflection simulating the image */}
            <div
              style={{
                position: "absolute",
                top: "-50%",
                left: "-10%",
                width: "120%",
                height: "130%",
                background:
                  "radial-gradient(ellipse 70% 50% at 50% 100%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 70%)",
                transform: "rotate(-4deg)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "45%",
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 100%)",
                pointerEvents: "none",
              }}
            />

            <div style={{ position: "relative", zIndex: 1 }}>
              <h2
                style={{
                  color: "#fff",
                  margin: 0,
                  fontWeight: 700,
                  fontSize: "1.4rem",
                  letterSpacing: "-0.01em",
                }}
              >
                Top Achievers of this Week
              </h2>
              <p
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  margin: "4px 0 0 0",
                  fontSize: "0.85rem",
                }}
              >
                {data?.week_start_date && data?.week_end_date
                  ? `${data.week_start_date} - ${data.week_end_date}`
                  : "Overall"}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {data?.nextMeetingDate && (
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <i
                    className="bx bxs-calendar-event"
                    style={{ fontSize: "1.1rem", color: "#a7f3d0" }}
                  ></i>
                  <span>
                    Next Meeting:{" "}
                    <span
                      style={{
                        color: "#fff",
                        marginLeft: "2px",
                        fontWeight: 700,
                      }}
                    >
                      {data.nextMeetingDate}
                    </span>
                  </span>
                </div>
              )}

              <button
                onClick={() => {
                  if (!excelLoading) handleDownloadExcel();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.12)",
                  border: "none",
                  color: "#e2e8f0",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: excelLoading ? "not-allowed" : "pointer",
                  opacity: excelLoading ? 0.7 : 1,
                  transition: "background 0.2s",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                onMouseOver={(e) => {
                  if (!excelLoading)
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.2)";
                }}
                onMouseOut={(e) => {
                  if (!excelLoading)
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.12)";
                }}
              >
                {excelLoading ? (
                  <>
                    Downloading... <i className="bx bx-loader-alt bx-spin"></i>
                  </>
                ) : (
                  <>
                    Download{" "}
                    <i
                      className="bx bx-right-arrow-alt"
                      style={{ fontSize: "1.2rem", marginTop: "1px" }}
                    ></i>
                  </>
                )}
              </button>
            </div>
          </div>

          <div
            className="card-body bg-white position-relative top-achievers-swiper-wrapper"
            style={{ padding: "16px" }}
          >
            <Swiper
              modules={[Navigation]}
              navigation
              spaceBetween={24}
              slidesPerView={"auto"}
              style={{ padding: "0 4px" }}
            >
              {activeCategories.map((cat) => (
                <SwiperSlide
                  key={cat.id}
                  style={{
                    width: "240px",
                    height: "auto",
                    display: "flex",
                  }}
                >
                  <div
                    className="top-achiever-card"
                    style={{
                      background: "rgb(243 244 246)",
                      borderRadius: "16px",
                      padding: "80px 16px 24px",
                      width: "100%",
                      flex: "1 1 0",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <MedalSVG />

                    <h4
                      style={{
                        color: "#115e59",
                        fontWeight: 800,
                        fontSize: "1.15rem",
                        textAlign: "center",
                        marginTop: "12px",
                        marginBottom: "24px",
                        lineHeight: "1.3",
                        textTransform: "capitalize",
                      }}
                    >
                      {cat.title}
                    </h4>

                    <div
                      style={{
                        width: "100%",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {ovalLoading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            flex: 1,
                            alignItems: "center",
                          }}
                        >
                          <OvalLoader color="#1a4331" />
                        </div>
                      ) : cat.list?.length > 0 ? (
                        cat.list.map((user, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              marginBottom: "20px",
                              width: "100%",
                            }}
                          >
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip className="top-achiever-tooltip custom-green-tooltip">
                                  {user.sno || i + 1}.{" "}
                                  {user[cat.tooltipNameKey] ||
                                    user[cat.nameKey]}{" "}
                                  ({user.user_company_name || ""}) -{" "}
                                  {user.country_name || ""} -{" "}
                                  {user.state_name || ""} -{" "}
                                  {user.region_name || ""} -{" "}
                                  {user.chapter_name || ""}
                                </Tooltip>
                              }
                            >
                              <img
                                src={
                                  user[cat.imgKey] ||
                                  user.thumb_profile_image ||
                                  user.thumb_user_profile_image ||
                                  user.thumb_from_profile_image ||
                                  user.profile_image ||
                                  user.user_profile_image ||
                                  user.from_profile_image
                                }
                                alt="profile"
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  flexShrink: 0,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  cursor: "pointer",
                                }}
                              />
                            </OverlayTrigger>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                              }}
                            >
                              <span
                                style={{
                                  color: "#17736b",
                                  fontWeight: 700,
                                  fontSize: "0.95rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {user[cat.nameKey]}
                              </span>
                              <span
                                style={{
                                  color: "#333",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {cat.formatVal(user[cat.valKey] || 0)}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px 0",
                            textAlign: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              backgroundColor: "rgba(0,0,0,0.04)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: "12px",
                            }}
                          >
                            <i
                              className="bx bx-award"
                              style={{ fontSize: "28px", color: "#94a3b8" }}
                            ></i>
                          </div>
                          <span
                            style={{
                              color: "#94a3b8",
                              fontSize: "0.9rem",
                              fontWeight: 500,
                            }}
                          >
                            No achievers yet
                          </span>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: "16px",
                        paddingTop: "16px",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.95rem",
                          color: "#000",
                          fontWeight: 700,
                        }}
                      >
                        Overall: {cat.overallValue || 0}
                      </span>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopAchievers;
